import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NavigationHeader from "../../components/ui/NavigationHeader";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Icon from "../../components/AppIcon";
import { fetchMercosAtendimentos, fetchMercosVisitas } from "./services/mercosApi";
import { formatBaseLabel } from "../../utils/baseDisplay";

const AUTO_REFRESH_MS = 180_000;
const PAGE_SIZE = 50;

const todayISO = () => new Date().toISOString().slice(0, 10);
const startOfMonthISO = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const emptyBlock = (base = "ALL") => ({
  base,
  period: null,
  sellers: [],
  rows: [],
  totals: {},
  meta: {},
  error: null,
});

const normalizeBlock = (block, fallbackBase = "ALL") => {
  if (!block || typeof block !== "object") return emptyBlock(fallbackBase);
  return {
    base: block.base || fallbackBase,
    period: block.period || null,
    sellers: Array.isArray(block.sellers) ? block.sellers : [],
    rows: Array.isArray(block.rows) ? block.rows : [],
    totals: block.totals && typeof block.totals === "object" ? block.totals : {},
    meta: block.meta && typeof block.meta === "object" ? block.meta : {},
    error: block.error || null,
  };
};

const normalizeResponse = (payload, fallbackBase = "ALL") => {
  const root = payload && typeof payload === "object" ? payload : {};
  const bases = root.bases && typeof root.bases === "object" ? root.bases : {};

  return {
    period: root.period || null,
    requestedBase: root.requestedBase || fallbackBase,
    kind: root.kind || "",
    warning: root.warning || "",
    cached: Boolean(root.cached),
    bases: {
      A: normalizeBlock(bases.A, "A"),
      B: normalizeBlock(bases.B, "B"),
    },
    combined: normalizeBlock(root.combined, root.requestedBase || fallbackBase),
    sellers: Array.isArray(root.sellers) ? root.sellers : [],
    rows: Array.isArray(root.rows) ? root.rows : [],
    totals: root.totals && typeof root.totals === "object" ? root.totals : {},
  };
};

const readCount = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
};

const formatPeriodLabel = (de, ate) => {
  if (!de && !ate) return "-";
  if (!de) return ate;
  if (!ate) return de;
  return `${de} até ${ate}`;
};

const normalizeBaseValue = (value) => {
  const base = String(value || "ALL").toUpperCase().trim();
  if (base === "A" || base === "B" || base === "ALL") return base;
  return "ALL";
};

const baseOptions = [
  { value: "ALL", label: formatBaseLabel("ALL") },
  { value: "A", label: formatBaseLabel("A") },
  { value: "B", label: formatBaseLabel("B") },
];

const tabOptions = [
  { value: "visitas", label: "Visitas com Check-in" },
  { value: "atendimentos", label: "Atendimentos" },
];

function SummaryCard({ title, value, subtitle, icon }) {
  return (
    <div className="rounded-2xl border border-[#f1d6c6] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-[#6b3a2a]">{title}</div>
        <Icon name={icon} size={16} />
      </div>
      <div className="mt-2 text-3xl font-bold text-[#3b1d0f]">{value}</div>
      <div className="text-xs text-[#6b3a2a]">{subtitle}</div>
    </div>
  );
}

function BaseStatusCard({ title, block, kind }) {
  const totals = block?.totals || {};
  const count = readCount(totals.count);
  const secondary =
    kind === "visitas"
      ? `${readCount(totals.concludedCount)} concluídas · ${readCount(totals.openCount)} em aberto`
      : `${readCount(totals.withDescricaoCount)} com descrição`;

  return (
    <div className="rounded-xl border border-[#f1d6c6] bg-[#fff7ed] p-3">
      <div className="text-xs font-semibold text-[#3b1d0f]">{title}</div>
      <div className="mt-2 text-lg font-semibold text-[#3b1d0f]">{count}</div>
      <div className="text-xs text-[#6b3a2a]">{secondary}</div>
    </div>
  );
}

function DataTable({ columns, rows, emptyMessage, rowKey }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-[#f1d6c6] text-left text-xs uppercase tracking-wide text-[#6b3a2a]">
            {columns.map((column) => (
              <th key={column.key} className="py-2 pr-4 last:pr-0">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="py-4 text-sm text-[#6b3a2a]" colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={rowKey(row, index)} className="border-b border-[#f7e7dc] align-top">
                {columns.map((column) => (
                  <td key={column.key} className="py-2 pr-4 last:pr-0 text-[#3b1d0f]">
                    {column.render ? column.render(row) : row[column.key] || "---"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaginationBar({ total, page, pageSize, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = total === 0 ? 0 : Math.min(total, safePage * pageSize);

  const pages = [];
  if (totalPages <= 7) {
    for (let p = 1; p <= totalPages; p += 1) pages.push(p);
  } else {
    const set = new Set([1, 2, safePage - 1, safePage, safePage + 1, totalPages - 1, totalPages]);
    Array.from(set)
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b)
      .forEach((p, index, arr) => {
        pages.push(p);
        const next = arr[index + 1];
        if (next && next - p > 1) pages.push("...");
      });
  }

  return (
    <div className="mt-4 flex flex-col gap-3 text-sm text-[#6b3a2a] md:flex-row md:items-center md:justify-between">
      <div>
        Resultados {start} - {end} de {total}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(1)}
          disabled={safePage === 1}
          className="rounded border border-[#f1d6c6] px-2 py-1 disabled:opacity-50"
        >
          «
        </button>
        <button
          type="button"
          onClick={() => onChange(safePage - 1)}
          disabled={safePage === 1}
          className="rounded border border-[#f1d6c6] px-2 py-1 disabled:opacity-50"
        >
          Anterior
        </button>

        {pages.map((item, index) => (
          item === "..." ? (
            <span key={`ellipsis-${index}`} className="px-2 py-1">...</span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onChange(item)}
              className={`rounded border px-2 py-1 ${
                item === safePage
                  ? "border-[#5b2d16] bg-[#5b2d16] text-white"
                  : "border-[#f1d6c6] bg-white text-[#6b3a2a]"
              }`}
            >
              {item}
            </button>
          )
        ))}

        <button
          type="button"
          onClick={() => onChange(safePage + 1)}
          disabled={safePage === totalPages}
          className="rounded border border-[#f1d6c6] px-2 py-1 disabled:opacity-50"
        >
          Próxima
        </button>
        <button
          type="button"
          onClick={() => onChange(totalPages)}
          disabled={safePage === totalPages}
          className="rounded border border-[#f1d6c6] px-2 py-1 disabled:opacity-50"
        >
          »
        </button>
      </div>
    </div>
  );
}

export default function MercosRotasDashboard() {
  const [base, setBase] = useState("ALL");
  const [de, setDe] = useState(startOfMonthISO());
  const [ate, setAte] = useState(todayISO());
  const [vendedorId, setVendedorId] = useState("");
  const [activeTab, setActiveTab] = useState("atendimentos");
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState("");
  const [visitasData, setVisitasData] = useState(normalizeResponse(null, "ALL"));
  const [atendimentosData, setAtendimentosData] = useState(normalizeResponse(null, "ALL"));
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const [visitasPage, setVisitasPage] = useState(1);
  const [atendimentosPage, setAtendimentosPage] = useState(1);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const safeBase = useMemo(() => normalizeBaseValue(base), [base]);

  const effectiveVisitas = useMemo(() => {
    if (safeBase === "A") return visitasData.bases.A;
    if (safeBase === "B") return visitasData.bases.B;
    return visitasData.combined;
  }, [safeBase, visitasData]);

  const effectiveAtendimentos = useMemo(() => {
    if (safeBase === "A") return atendimentosData.bases.A;
    if (safeBase === "B") return atendimentosData.bases.B;
    return atendimentosData.combined;
  }, [safeBase, atendimentosData]);

  const sellerOptions = useMemo(() => {
    const map = new Map();
    const sellers = activeTab === "visitas" ? effectiveVisitas?.sellers || [] : effectiveAtendimentos?.sellers || [];

    sellers.forEach((seller) => {
      const sellerKey = seller?.sellerKey || `${seller?.base || safeBase}:${seller?.sellerId || ""}`;
      if (!sellerKey) return;
      map.set(sellerKey, {
        value: sellerKey,
        label: `${seller?.sellerName || seller?.sellerId || "Sem nome"}${seller?.base ? ` (${formatBaseLabel(seller.base)})` : ""}`,
      });
    });

    return [{ value: "", label: "Todos os vendedores" }, ...Array.from(map.values())].sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), "pt-BR")
    );
  }, [activeTab, safeBase, effectiveAtendimentos, effectiveVisitas]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!vendedorId) return;
    const exists = sellerOptions.some((option) => option.value === vendedorId);
    if (!exists) setVendedorId("");
  }, [sellerOptions, vendedorId]);

  const applyWarningFromDataset = useCallback((dataset) => {
    const warnings = [];
    if (dataset?.warning) warnings.push(dataset.warning);
    if (dataset?.bases?.A?.error?.message) warnings.push(`${formatBaseLabel("A")}: ${dataset.bases.A.error.message}`);
    if (dataset?.bases?.B?.error?.message) warnings.push(`${formatBaseLabel("B")}: ${dataset.bases.B.error.message}`);
    setWarning(Array.from(new Set(warnings)).join(" | "));
  }, []);

  const resetPages = useCallback(() => {
    setVisitasPage(1);
    setAtendimentosPage(1);
  }, []);

  const load = useCallback(async () => {
    if (!de || !ate) {
      setWarning("Preencha o período antes de consultar.");
      return;
    }

    if (de > ate) {
      setWarning("A data inicial não pode ser maior que a data final.");
      return;
    }

    const requestId = ++requestIdRef.current;
    const currentBase = normalizeBaseValue(base);
    const currentTab = activeTab;
    const currentParams = { base: currentBase, de, ate, vendedorId };

    setLoading(true);
    setWarning("");

    try {
      const response = currentTab === "visitas"
        ? await fetchMercosVisitas(currentParams)
        : await fetchMercosAtendimentos(currentParams);

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      const normalized = normalizeResponse(response, currentBase);

      if (currentTab === "visitas") {
        setVisitasData(normalized);
        setVisitasPage(1);
      } else {
        setAtendimentosData(normalized);
        setAtendimentosPage(1);
      }

      applyWarningFromDataset(normalized);
      setLastUpdatedAt(new Date().toLocaleString("pt-BR"));
    } catch (error) {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      setWarning(error?.message || "Erro ao buscar dados do Mercos");
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab, applyWarningFromDataset, base, de, ate, vendedorId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        load();
      }
    }, AUTO_REFRESH_MS);

    return () => clearInterval(timer);
  }, [load]);

  const activeBlock = activeTab === "visitas" ? effectiveVisitas : effectiveAtendimentos;
  const activeTotals = activeBlock?.totals || {};
  const activeRows = Array.isArray(activeBlock?.rows) ? activeBlock.rows : [];
  const sellerCount = Array.isArray(activeBlock?.sellers) ? activeBlock.sellers.length : 0;
  const currentPage = activeTab === "visitas" ? visitasPage : atendimentosPage;
  const totalPages = Math.max(1, Math.ceil(activeRows.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(() => {
    if (activeTab === "visitas" && visitasPage !== safePage) {
      setVisitasPage(safePage);
    }
    if (activeTab === "atendimentos" && atendimentosPage !== safePage) {
      setAtendimentosPage(safePage);
    }
  }, [activeTab, atendimentosPage, safePage, visitasPage]);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return activeRows.slice(start, start + PAGE_SIZE);
  }, [activeRows, safePage]);

  const visitsColumns = useMemo(
    () => [
      { key: "colaboradorNome", label: "Vendedor", render: (row) => row.colaboradorNome || "---" },
      { key: "razaoSocial", label: "Razão social", render: (row) => row.razaoSocial || row.clienteNomeFantasia || "---" },
      { key: "dataHoraCheckinLabel", label: "Data/hora Check-in", render: (row) => row.dataHoraCheckinLabel || "---" },
      { key: "dataHoraCheckoutLabel", label: "Data/hora Check-out", render: (row) => row.dataHoraCheckoutLabel || "Em aberto" },
      { key: "duracao", label: "Duração", render: (row) => row.duracao || "Em aberto" },
      { key: "statusCheckin", label: "Status Check-in", render: (row) => row.statusCheckin || "---" },
      { key: "statusCheckout", label: "Status Check-out", render: (row) => row.statusCheckout || "---" },
      { key: "pedido", label: "Pedido", render: (row) => row.pedido || "---" },
      { key: "base", label: "Empresa", render: (row) => formatBaseLabel(row.base) },
    ],
    []
  );

  const atendimentosColumns = useMemo(
    () => [
      { key: "dataInicioLabel", label: "Data início", render: (row) => row.dataInicioLabel || "---" },
      { key: "razaoSocial", label: "Razão social", render: (row) => row.razaoSocial || row.clienteNomeFantasia || "---" },
      { key: "resultado", label: "Resultado", render: (row) => row.resultado || "---" },
      { key: "descricao", label: "Descrição", render: (row) => row.descricao || "---" },
      { key: "tarefa", label: "Tarefa", render: (row) => row.tarefa || "---" },
      { key: "tipo", label: "Tipo", render: (row) => row.tipo || "---" },
      { key: "colaboradorNome", label: "Colaborador", render: (row) => row.colaboradorNome || "---" },
      { key: "base", label: "Empresa", render: (row) => formatBaseLabel(row.base) },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-[#fff7ed]">
      <NavigationHeader />

      <main className="mx-auto max-w-7xl px-6 pb-10 pt-24">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#3b1d0f]">Integração Mercos - Rotas e Atendimentos</h1>
            <p className="text-sm text-[#6b3a2a]">
              Tabelas no formato do Mercos com dados reais de visitas com check-in e atendimentos.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-xs text-[#6b3a2a]">
              Auto-refresh: <span className="font-medium">180s</span>
            </div>
            <Button onClick={load} disabled={loading}>
              <span className="inline-flex items-center gap-2">
                <Icon name="RefreshCw" size={16} className={loading ? "animate-spin" : ""} />
                {loading ? "Atualizando..." : "Atualizar"}
              </span>
            </Button>
          </div>
        </div>

        {warning ? (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">
            <div className="font-medium">Avisos da integração</div>
            <div className="text-sm">{warning}</div>
          </div>
        ) : null}

        <div className="mt-6 rounded-2xl border border-[#f1d6c6] bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Empresa</div>
              <Select
                value={safeBase}
                onChange={(value) => {
                  setBase(normalizeBaseValue(value));
                  setWarning("");
                  resetPages();
                }}
                options={baseOptions}
              />
            </div>

            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">De</div>
              <Input type="date" value={de} onChange={(e) => { setDe(e.target.value); resetPages(); }} />
            </div>

            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Até</div>
              <Input type="date" value={ate} onChange={(e) => { setAte(e.target.value); resetPages(); }} />
            </div>

            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Vendedor</div>
              <Select
                value={vendedorId}
                onChange={(value) => {
                  setVendedorId(value);
                  resetPages();
                }}
                options={sellerOptions}
                searchable
                clearable
              />
            </div>

            <div className="flex items-end">
              <Button className="w-full" onClick={load} disabled={loading}>
                Recarregar dados
              </Button>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2 text-xs text-[#6b3a2a] md:flex-row md:items-center md:justify-between">
            <div>
              Período consultado: <span className="font-medium">{formatPeriodLabel(de, ate)}</span>
            </div>
            <div className="flex flex-col text-right">
              <span>
                Tab ativa: <span className="font-medium">{tabOptions.find((tab) => tab.value === activeTab)?.label}</span>
              </span>
              {lastUpdatedAt ? (
                <span>
                  Última atualização: <span className="font-medium">{lastUpdatedAt}</span>
                </span>
              ) : null}
            </div>
          </div>

          {safeBase === "ALL" ? (
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <BaseStatusCard title={formatBaseLabel("A")} block={activeTab === "visitas" ? visitasData.bases.A : atendimentosData.bases.A} kind={activeTab} />
              <BaseStatusCard title={formatBaseLabel("B")} block={activeTab === "visitas" ? visitasData.bases.B : atendimentosData.bases.B} kind={activeTab} />
            </div>
          ) : null}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-4">
          <SummaryCard
            title={activeTab === "visitas" ? "Total de visitas" : "Total de atendimentos"}
            value={readCount(activeTotals.count)}
            subtitle={activeTab === "visitas" ? "Registros no período" : "Linhas prontas para a tabela"}
            icon={activeTab === "visitas" ? "MapPinned" : "ClipboardList"}
          />

          <SummaryCard
            title={activeTab === "visitas" ? "Check-out concluído" : "Com descrição"}
            value={activeTab === "visitas" ? readCount(activeTotals.concludedCount) : readCount(activeTotals.withDescricaoCount)}
            subtitle={activeTab === "visitas" ? "Visitas finalizadas" : "Atendimentos preenchidos"}
            icon={activeTab === "visitas" ? "CheckCircle2" : "MessageSquareText"}
          />

          <SummaryCard
            title={activeTab === "visitas" ? "Em aberto" : "Vendedores"}
            value={activeTab === "visitas" ? readCount(activeTotals.openCount) : sellerCount}
            subtitle={activeTab === "visitas" ? "Visitas sem check-out" : "Colaboradores com registros"}
            icon={activeTab === "visitas" ? "Clock3" : "Users"}
          />

          <SummaryCard
            title="Empresa atual"
            value={formatBaseLabel(safeBase)}
            subtitle="Filtro aplicado"
            icon="Database"
          />
        </div>

        <div className="mt-6 rounded-2xl border border-[#f1d6c6] bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap gap-2">
            {tabOptions.map((tab) => {
              const active = tab.value === activeTab;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.value);
                    setWarning("");
                    resetPages();
                  }}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    active ? "bg-[#5b2d16] text-white" : "border border-[#f1d6c6] bg-[#fff7ed] text-[#6b3a2a]"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === "visitas" ? (
            <>
              <div className="mb-3">
                <div className="text-sm font-semibold text-[#3b1d0f]">Visitas com Check-in</div>
                <div className="text-xs text-[#6b3a2a]">
                  Layout pronto para exibir vendedor, cliente, check-in, check-out, duração, status, pedido e empresa.
                </div>
              </div>

              <DataTable
                columns={visitsColumns}
                rows={pagedRows}
                emptyMessage="Nenhuma visita encontrada para o período e filtro selecionados."
                rowKey={(row, index) => `${row.id || row.dataHoraCheckin || row.dataInicio || "visita"}-${row.base}-${index}`}
              />
            </>
          ) : (
            <>
              <div className="mb-3">
                <div className="text-sm font-semibold text-[#3b1d0f]">Atendimentos</div>
                <div className="text-xs text-[#6b3a2a]">
                  Payload pronto para Data início, Razão social, Resultado, Descrição, Tarefa, Tipo, Colaborador e Empresa.
                </div>
              </div>

              <DataTable
                columns={atendimentosColumns}
                rows={pagedRows}
                emptyMessage="Nenhum atendimento encontrado para o período e filtro selecionados."
                rowKey={(row, index) => `${row.id || row.dataInicio || "atendimento"}-${row.base}-${index}`}
              />
            </>
          )}

          <PaginationBar
            total={activeRows.length}
            page={safePage}
            pageSize={PAGE_SIZE}
            onChange={(page) => {
              if (activeTab === "visitas") setVisitasPage(page);
              else setAtendimentosPage(page);
            }}
          />
        </div>
      </main>
    </div>
  );
}
